import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  InsuranceQuestionDto,
  QuestionType,
} from '../../../../core/data/dtos/insurance-question.dto';
import { InsuranceQuestionGroupDto } from '../../../../core/data/dtos/insurance-question-group.dto';

/** Upload status of a file answer; files upload as soon as they are selected. */
export interface FileUploadState {
  status: 'uploading' | 'done' | 'error';
  /** 0-100 */
  progress: number;
  /** Upload id stored as the answer value once done. */
  uploadId?: string;
  error?: string;
}

@Component({
  selector: 'app-checkout-questions',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './checkout-questions.component.html',
  styleUrls: ['./checkout-questions.component.scss'],
})
export class CheckoutQuestionsComponent {
  @Input({ required: true }) form!: FormGroup;
  @Input() activeGroup: InsuranceQuestionGroupDto | null = null;
  @Input() fileSelections: Map<number, File> = new Map();
  @Input() uploadStates: Map<number, FileUploadState> = new Map();
  @Output() retryUpload = new EventEmitter<number>();
  @Output() fileSelected = new EventEmitter<{
    questionId: number;
    file: File;
  }>();
  @Output() fileRemoved = new EventEmitter<number>();

  readonly fileErrors = new Map<number, string>();
  readonly QuestionType = QuestionType;

  get visibleQuestions(): InsuranceQuestionDto[] {
    if (!this.activeGroup?.questions) return [];
    return this.activeGroup.questions.filter((q) => !q.hideOnCheckout);
  }

  qKey(id: number): string {
    return 'q_' + id;
  }

  onFileChange(questionId: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    this.fileErrors.delete(questionId);

    if (input.files?.length) {
      const file = input.files[0];
      const maxSize = 40 * 1024 * 1024; // 40MB

      if (file.size > maxSize) {
        this.fileErrors.set(questionId, 'El archivo supera el límite de 40MB.');
        input.value = ''; // clear the input
        this.fileRemoved.emit(questionId);
      } else {
        this.fileSelected.emit({ questionId, file });
      }
    }
  }

  getSelectedFileName(questionId: number): string | null {
    const file = this.fileSelections.get(questionId);
    if (file) return file.name;
    // Fallback: check if there's a previously saved value in the form control
    const ctrl = this.form.get(this.qKey(questionId));
    if (ctrl?.value) return 'Documento cargado';
    return null;
  }

  removeFile(questionId: number): void {
    this.fileErrors.delete(questionId);
    this.fileRemoved.emit(questionId);
    // Reset form control so required validation triggers again
    const ctrl = this.form.get(this.qKey(questionId));
    if (ctrl) {
      ctrl.setValue(null);
      ctrl.markAsTouched();
    }
  }
}
